import type { GetMeUserResponse } from "@nlc/shared-types";

import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";

import { ConfirmationModal } from "@/components/confirmation-modal";
import { toast } from "@/components/toast/toast-queue";
import { usersKeys } from "@/hooks/queryKeys";

import { useUnarchiveUser } from "../hooks/useUnarchiveUser";

export default function UnarchiveUser({
  onClose,
  openModal,
  userdata,
}: {
  onClose: () => void;
  openModal: boolean;
  userdata: GetMeUserResponse | undefined;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isOpenModal, setIsOpenModal] = useState(false);
  const user = userdata?.user;
  const { isPending, mutate } = useUnarchiveUser(user?.id);

  useEffect(() => {
    if (openModal) {
      setIsOpenModal(true);
    }
    if (!isOpenModal) onClose();
  }, [openModal, isOpenModal]);

  if (!userdata || !user) {
    return null;
  }

  const archiveUser = () => {
    mutate(undefined, {
      onError: (data) => {
        toast.add({
          description: data.message,
          timeout: 5000,
          title: data.name,
          type: "error",
        });
      },
      onSuccess: () => {
        toast.add({
          description: `User ${user.name} was successfully unarchived.`,
          timeout: 5000,
          title: "User unarchived",
          type: "success",
        });

        navigate({ to: "/users" });

        return queryClient.invalidateQueries({
          queryKey: usersKeys.all,
        });
      },
    });
  };

  return (
    <ConfirmationModal
      confirmButtonColor="color"
      confirmButtonText="Unarchive"
      isConfirmLoading={isPending}
      isOpen={isOpenModal}
      onConfirm={archiveUser}
      onOpenChange={setIsOpenModal}
      text={`Are you sure you want to unarchive user ${user.name} - ${user.email}?`}
      title={`Unarchive user: ${user.name}`}
    />
  );
}
